/**
 * Helpers for identifying the agent from Connect SDK fields.
 */

/**
 * Agent ID allowlist — used as a client-side gate for dogfood features
 * (e.g. Customer 360). Contains bare agent UUIDs (the last segment of the
 * Connect agent ARN, after `/agent/`). Portable across instances/accounts.
 *
 * This is a UX gate only. Do NOT rely on it for authorization — it is
 * trivially bypassable in the browser. Authoritative gating must live on
 * the backend.
 *
 * TODO: Replace hardcoded Traeger/Calltree agent UUIDs with a server-driven
 * allowlist (e.g. from AppConfig feature flag targeting rules or a backend
 * endpoint). This list should not live in the base webapp.
 */
const AGENT_ID_ALLOWLIST: ReadonlySet<string> = new Set<string>([
  // Personal
  "598ed138-2017-41b1-b3fc-7b50a023a913", // masayuki
  "5fdba4ba-d2aa-47bb-855a-4694e5678561", // masayuki@calltree.ai
  // Dev
  "e95b333c-44e9-409c-9320-bab1a96f98fe", // agent1
  "5764fbdc-e8a2-445c-9d53-930c10309c68", // kun@calltree.ai
  "2c22d78b-8585-4531-8b49-5362343c4695", // masayuki
  "8851aaf1-3b26-4733-a5e7-4fe0bd695324", // rob
  "528faf94-2b8e-46a9-9961-74acfad6a21d", // janusz
  // Prod
  "7ef2c822-14c0-4425-8bb3-db7bc9760d74", // anis.egerer@calltree.ai
  "94479ed9-d281-471e-ab55-1180cd23dbdd", // ben.samuelson@calltree.ai
  "0a229c77-6cc6-4128-b74b-93de72fa266e", // calltree_admin
  "2816efa2-5816-4faf-9710-496665a84752", // calltree_fullaccess
  "6dbd1fbc-dcf6-44dc-8a1f-0a0b4e9e8d51", // calltree_readonly
  "b9383a2d-5b50-48cf-b621-f1e295e48f80", // daniel.hinojosa@calltree.ai
  "39c1301b-d5c3-4ae0-9158-74c9e8e24014", // joshua.smith@calltree.ai
  "ede8448e-7008-43a5-a548-d842e768f6a0", // katherine@calltree.ai
  "005c61ce-e00c-474d-b7c4-f98805fe7b42", // lydell.ware@calltree.ai
  "cfe35caa-637f-4a21-9b38-ac878aa4f2a0", // noah.barron@calltree.ai
]);

/** Extract the agent UUID from a Connect agent ARN, or return null. */
function extractAgentId(agentArn: string | null | undefined): string | null {
  if (!agentArn) return null;
  const parts = agentArn.split("/");
  // arn:aws:connect:…:instance/<iid>/agent/<aid>
  const agentIdx = parts.indexOf("agent");
  if (agentIdx === -1 || agentIdx + 1 >= parts.length) return null;
  return parts[agentIdx + 1] || null;
}

export function isAllowlistedAgent(agentArn: string | null | undefined): boolean {
  const id = extractAgentId(agentArn);
  return id !== null && AGENT_ID_ALLOWLIST.has(id);
}
